import {
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Typography
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import { CONFETTI_DURATION, CONFETTI_GRAVITY, CONFETTI_PIECES, DESCRIPTION_MAX_LENGTH, MODAL_MAX_WIDTH } from '../utils/constants';
import { truncateDescription } from '../utils/helpers';

const NotSeenAchievementsModal = ({
    open,
    onClose,
    achievements = [],
    onViewAchievements,
    getAchievementColors
}) => {
    const navigate = useNavigate();
    const [runConfetti, setRunConfetti] = useState(false);
    const confettiWidth = window.innerWidth;
    const confettiHeight = window.innerHeight;

    // Trigger confetti when modal opens
    useEffect(() => {
        if (open && achievements.length > 0) {
            setRunConfetti(true);
            // Stop confetti after configured duration
            const timer = setTimeout(() => {
                setRunConfetti(false);
            }, CONFETTI_DURATION);
            return () => clearTimeout(timer);
        }
    }, [open, achievements.length]);

    const handleViewInHall = () => {
        onClose();
        navigate('/hall-of-honor');
    };

    return (
        <>
            <Confetti
                width={confettiWidth}
                height={confettiHeight}
                run={runConfetti}
                numberOfPieces={CONFETTI_PIECES}
                recycle={false}
                gravity={CONFETTI_GRAVITY}
                key={runConfetti}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    zIndex: 9999,
                    pointerEvents: 'none'
                }}
            />
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={MODAL_MAX_WIDTH}
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconComponent iconName="trophy" style={{ color: '#fbbf24', fontSize: 24 }} />
                        <Typography variant="h6">
                            ¡Nuevos Logros Desbloqueados! ({achievements.length})
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={2}>
                        {achievements.map((achievement) => {
                            const difficultyColors = getAchievementColors ? getAchievementColors(achievement.achievement_type) : {};

                            return (
                                <Grid item xs={12} key={achievement.achievement_progress_id}>
                                    <Card
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'flex-start',
                                            p: 2,
                                            '&:hover': {
                                                boxShadow: 3,
                                                transform: 'translateY(-2px)',
                                                transition: 'all 0.2s ease-in-out'
                                            }
                                        }}
                                    >
                                        {/* Image on the left */}
                                        <Box sx={{ mr: 2, flexShrink: 0 }}>
                                            <Avatar
                                                src={achievement.signed_thumbnail_url || achievement.thumbnail_url || ''}
                                                alt={achievement.achievement_name}
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    objectFit: 'scale-down',
                                                    bgcolor: '#f5f5f5'
                                                }}
                                            />
                                        </Box>

                                        {/* Content on the right */}
                                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                <IconComponent
                                                    iconName="trophy"
                                                    style={{ fontSize: 16, color: '#fbbf24' }}
                                                />
                                                <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                                                    {achievement.achievement_name}
                                                </Typography>
                                            </Box>

                                            <Chip
                                                label={achievement.achievement_type}
                                                size="small"
                                                sx={{
                                                    ...difficultyColors,
                                                    fontSize: '10px',
                                                    fontWeight: 600,
                                                    alignSelf: 'flex-start',
                                                    mb: 1
                                                }}
                                            />

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ flexGrow: 1, mb: 1 }}
                                            >
                                                {truncateDescription(achievement.achievement_description || achievement.description, DESCRIPTION_MAX_LENGTH)}
                                            </Typography>

                                            {achievement.completed_at && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Completado: {new Date(achievement.completed_at).toLocaleDateString()}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>
                        Cerrar
                    </Button>
                    <Button
                        onClick={handleViewInHall}
                        variant="contained"
                        startIcon={<IconComponent iconName="trophy" />}
                    >
                        Ver en Hall of Honor
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default NotSeenAchievementsModal; 